import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Trash2, Download, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from './backendConfig';
import './TicketFiles.css';

/* Files attached to a ticket — Max, 2026-09-07: "có thể thêm tính năng đính kèm file trong mỗi
   ticket hem". They go out as REAL email attachments, which is the choice he made when asked, so
   what is uploaded here is what lands in the receiving team's inbox.

   Only for a ticket that already EXISTS. A file belongs to a task_id, and a ticket being staged has
   none — the same gate the SKU material list uses, and for the same reason: there is nothing to hang
   the row off yet. The card says so rather than showing a control that would fail.

   One file per request, base64. It is what the portal's photo upload already does: no multer in the
   server, and a dropped connection costs one file instead of ten. */

const API = `${BACKEND_URL}/api/development`;
const MAX_MB = 10;

export const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function TicketFiles({ taskId, disabled = false }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    try {
      const r = await fetch(`${API}/tickets/${encodeURIComponent(taskId)}/files`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (r.ok) setFiles(d.files || []);
    } catch (e) { /* a list that fails to load is not worth an error banner over the form */ }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const send = async (file) => {
    // Read as base64. The size is checked here so a 40 MB pick is refused before it is read into
    // memory, and again on the server, which is the one that actually decides.
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(t('st.tfTooBig', { name: file.name, mb: (file.size / 1048576).toFixed(1), max: MAX_MB }));
      return;
    }
    const dataBase64 = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
      fr.onerror = () => reject(new Error('read failed'));
      fr.readAsDataURL(file);
    });
    const r = await fetch(`${API}/tickets/${encodeURIComponent(taskId)}/files`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ fileName: file.name, dataBase64 }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `Upload failed (${r.status})`);
    setFiles(d.files || []);
  };

  const onPick = async (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = '';                       // so the same file can be picked again after a delete
    if (!picked.length) return;
    setBusy(true); setError('');
    try {
      // One at a time, in order: the server caps the total, and a sequential loop is what makes the
      // refusal land on the file that broke the cap rather than on an arbitrary one.
      for (const f of picked) await send(f);   // eslint-disable-line no-await-in-loop
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const remove = async (f) => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/tickets/files/${f.fileId}`, { method: 'DELETE', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Delete failed (${r.status})`);
      setFiles(d.files || []);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  // Either a saved ticket or a draft key — both are keys the server accepts. Only a card with
  // neither has nothing to hang a file off, which in practice means a read-only view.
  if (!taskId) return <div className="tf-none">{t('st.tfSaveFirst')}</div>;
  const isDraft = /^draft-/.test(String(taskId));

  const total = files.reduce((sum, f) => sum + (f.bytes || 0), 0);

  return (
    <div className="tf-wrap">
      {!!files.length && (
        <ul className="tf-list">
          {files.map((f) => (
            <li key={f.fileId}>
              <Paperclip size={13} />
              {/* A plain link: the endpoint sends Content-Disposition, so the browser downloads it
                  under the name that was uploaded rather than rendering it in the tab. */}
              <a href={`${BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer">{f.name}</a>
              <span className="tf-size">{fmtBytes(f.bytes)}</span>
              {!disabled && (
                <button type="button" className="tf-x" title={t('st.tfRemove')} disabled={busy}
                  onClick={() => remove(f)}>
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        <>
          <button type="button" className="tf-drop" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? <Loader size={15} className="tf-spin" /> : <Download size={15} />}
            <span className="tf-drop-main">{busy ? t('st.tfUploading') : t('st.tfPick')}</span>
            <span className="tf-drop-sub">{t('st.tfHint', { max: MAX_MB })}</span>
          </button>
          <input ref={input} type="file" multiple className="tf-input" onChange={onPick} />
        </>
      )}

      {!!files.length && (
        <div className="tf-total">
          {t('st.tfTotal', { n: files.length, size: fmtBytes(total) })}
          {isDraft && ` — ${t('st.tfDraftNote')}`}
        </div>
      )}
      {!!error && <div className="tf-err">{error}</div>}
    </div>
  );
}
